/**
 * Notification Component
 * 
 * Displays slide-up notifications from bottom of screen
 * Triggered by URL parameters (e.g., ?customer_posted=true)
 * Auto-hides after duration or on close button click
 */

class NotificationComponent extends HTMLElement {
  constructor() {
    super();
    
    // Component properties
    this.autoHideTimer = null;
    this.autoHideDuration = 5000; // 5 seconds
    this.isVisible = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
  
    // Move notification to notifications-root wrapper (like modal does)
    const notificationsRoot = document.getElementById('notifications-root');
    if (notificationsRoot && this.parentElement !== notificationsRoot) {
      notificationsRoot.appendChild(this);
    }
  
    // Get notification type from element ID
    const notificationType = this.id.replace('notification-', '');
    
    // Check if we should show based on URL
    const shouldShow = this.checkUrlParams(notificationType);
    
    if (shouldShow) {
      this.setupCloseButton();
      
      requestAnimationFrame(() => {
        this.showNotification();
      });
      
      this.autoHideTimer = setTimeout(() => {
        this.hideNotification();
        this.removeUrlParam('customer_posted');
      }, this.autoHideDuration);
    }
  }

  /**
   * Check URL parameters to determine if notification should show
   * @param {string} notificationType - Type of notification (success, error, etc.)
   * @returns {boolean}
   */
  checkUrlParams(notificationType) {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for success notification
    if (notificationType === 'success' && urlParams.get('customer_posted') === 'true') {
      return true;
    }
    
    // Add more notification types here if needed
    // if (notificationType === 'error' && urlParams.has('form_error')) {
    //   return true;
    // }
    
    return false;
  }

  /**
   * Show notification with slide-up animation
   */
  showNotification() {
    this.isVisible = true;
    this.setAttribute('aria-hidden', 'false');
    
    // Remove translate-y-full and opacity-0 to slide up and fade in
    this.classList.remove('translate-y-full', 'opacity-0');
    this.classList.add('translate-y-0', 'opacity-100');
  }

  /**
   * Hide notification with slide-down animation
   */
  hideNotification() {
    this.isVisible = false;
    this.setAttribute('aria-hidden', 'true');
    
    // Add translate-y-full and opacity-0 to slide down and fade out
    this.classList.remove('translate-y-0', 'opacity-100');
    this.classList.add('translate-y-full', 'opacity-0');
    
    // Clear auto-hide timer if it exists
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
      this.autoHideTimer = null;
    }
  }

  /**
   * Remove URL parameter and hash
   * @param {string} param - Parameter name to remove
   */
  removeUrlParam(param) {
    const url = new URL(window.location);
    
    // Remove the parameter
    url.searchParams.delete(param);
    
    // Remove the hash
    url.hash = '';
    
    // Update URL without page reload
    window.history.replaceState({}, '', url);
  }

  /**
   * Set up close button event listener
   */
  setupCloseButton() {
    const closeBtn = this.querySelector('[data-modal-close]');
    
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hideNotification();
        this.removeUrlParam('customer_posted');
      });
    }
  }

  /**
   * Clean up when element is removed from DOM
   */
  disconnectedCallback() {
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
    }
  }
}

// Define the custom element
customElements.define('notification-component', NotificationComponent);
